import { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAxiosError } from 'axios';
import Navbar from '../../Navbar/Navbar';
import InfoSegment from '../InfoSegment/InfoSegment';
import MemberSegment from '../MemberSegment/MemberSegment';
import AttachmentSegment from '../AttachmentSegment/AttachmentSegment';
import { IInfoInput } from '../InfoSegment/InfoSegment';
import { IMemberWrapper } from '../MemberSegment/MemberSegment';
import './BandPage.css';

interface IMemberDB {
  id: number;
  firstName: string;
  lastName: string;
  instrument: string;
  microphone: string;
  phone: string;
}

export interface IAttachmentDB {
  picture: string;
  position: string;
}

const BandPage = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const bandId = sessionStorage.getItem('editedBandId');

  const INFO_URL = bandId ? `/admin/bands/${bandId}` : '/bands';
  const MEMBER_URL =  bandId ? `/admin/bandMembers/${bandId}` : '/bandMembers';
  const ATTACHMENT_URL = bandId ? `/admin/images/${bandId}` : '/images';

  const [info, setInfo] = useState<IInfoInput>({
    name: '',
    nominated: false,
    festive: '',
    cities: '',
    trivia: '',
  });
  const [members, setMembers] = useState<IMemberWrapper[]>([]);
  const [attachment, setAttachment] = useState<IAttachmentDB>({
    picture: '',
    position: '',
  });

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const getInfo = async () => {
      try {
        const response = await axiosPrivate.get(INFO_URL, {
          signal: controller.signal,
        });

        const { name, nominated, festive, cities, trivia } = response.data;

        isMounted &&
          setInfo({
            name: name ?? '',
            nominated: nominated === 'tak',
            festive: festive ?? '',
            cities: cities ?? '',
            trivia: trivia ?? '',
          });
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response && error.response.status === 403) {
            navigate('/', { state: { from: location }, replace: true });
          }
        }
      }
    };

    const getMembers = async () => {
      try {
        const response = await axiosPrivate.get(MEMBER_URL, {
          signal: controller.signal,
        });

        response.data.forEach((m: IMemberDB) =>
          setMembers((prev) => [
            ...prev,
            {
              id: m.id,
              inDB: true,
              action: 'none',
              input: {
                firstName: m.firstName,
                lastName: m.lastName,
                instrument: m.instrument,
                microphone: m.microphone,
                phone: m.phone,
              },
            },
          ])
        );
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response && error.response.status === 403) {
            navigate('/', { state: { from: location }, replace: true });
          }
        }
      }
    };

    const getAttachments = async () => {
      try {
        const response = await axiosPrivate.get(ATTACHMENT_URL, {
          signal: controller.signal,
        });

        setAttachment({
          position: response.data?.setting,
          picture: response.data?.band,
        });
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response && error.response.status === 403) {
            navigate('/', { state: { from: location }, replace: true });
          }
        }
      }
    };

    getInfo();
    getMembers();
    getAttachments();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  return (
    <div className="band-container">
      <Navbar />
      <div className="band-content">
        <InfoSegment data={info} />
        <MemberSegment data={members} />
        <AttachmentSegment data={attachment} />
      </div>
    </div>
  );
};

export default BandPage;
